import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { conflictsWithBuffer } from '../../lib/booking-buffer';
import {
    CameraIcon,
    VideoCameraIcon,
    ComputerDesktopIcon,
    RocketLaunchIcon,
    MapPinIcon,
    ClockIcon,
    CalendarIcon,
    UserIcon,
    EnvelopeIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ArrowLongRightIcon,
    CheckCircleIcon,
    BoltIcon,
} from '@heroicons/react/24/outline';
import ExpressBookingModal from '../components/booking/ExpressBookingModal';
import TurnstileField, { turnstileRequired } from '../components/TurnstileField';
import BrandAssetPicker from '../components/booking/BrandAssetPicker';
import { sendFilesToUploadLink } from '../lib/clientUploadSend';

// ── Service data ──────────────────────────────────────────────────────────────

const PHOTO_SERVICES = [
    {
        id: 'portrait',
        label: 'LIFESTYLE PORTRAIT',
        price: '$250',
        meta: '30–45 min · Downtown Austin',
        eventType: 'Lifestyle Shoot',
        features: [
            '10–15 curated photos',
            'Same-day delivery',
            'Candid, lifestyle direction',
        ],
    },
    {
        // Priced to enter the Austin short-term-rental market competitively.
        // Reference points (2026): Austin MLS listing work runs $150–325 with a
        // ~$185 median; STR shoots run $100–400 for a standard unit, with budget
        // operators from $149. A $195 entry undercuts the mid-market without
        // landing in the budget bracket, and the ladder carries the margin on
        // larger properties. Add-ons track market: drone $95–400, 3D $100–525.
        id: 'airbnb',
        label: 'AIRBNB / SHORT-TERM RENTAL',
        price: '$195+',
        meta: 'Studio / 1BR · Your property',
        eventType: 'Airbnb / Short-Term Rental',
        features: [
            '25–35 edited photos · 24–72 hr delivery',
            '2BR $265 · 3BR $335 · 4BR+ / luxury from $425',
            'Twilight +$125 · Drone +$150 · 3D tour +$200',
        ],
    },
    {
        // Multifamily / property-management work. Distinct from the Airbnb tier
        // above: a management company is not buying one listing, it is buying
        // model units, amenity spaces and exteriors across a portfolio, and the
        // scope is set by the property rather than by a bedroom count.
        //
        // Same ladder logic as the $195 Airbnb entry — a single unit enters just
        // above the STR rate (leasing photography carries more amenity and
        // exterior coverage), and the margin is carried by the property package
        // and the portfolio retainer. Quoted on a call rather than auto-invoiced:
        // a 12-unit boutique building and a 300-unit complex cannot share one
        // fixed price, and billing either from a card would be wrong.
        id: 'realestate',
        label: 'REAL ESTATE / MULTIFAMILY',
        price: '$225+',
        meta: 'Per unit or per property · Austin metro',
        eventType: 'Real Estate / Multifamily',
        isConsultation: true,
        features: [
            'Single model unit or vacant listing $225 · 20–30 edited photos',
            'Property package $850 — exteriors, amenities + 2 model units',
            'Portfolio retainer from $1,600/mo · Drone +$150 · 3D tour +$200',
        ],
    },
    {
        id: 'event',
        label: 'EVENT COVERAGE',
        price: '$600',
        meta: '3 hours · Your venue',
        eventType: 'Event Coverage',
        features: [
            '20–30 curated photos, next-day',
            'Event highlight reel within 48 hrs',
            '+$175/hr for additional hours',
        ],
    },
    {
        id: 'halfday',
        label: 'HALF-DAY CONTENT',
        price: '$800',
        meta: '4 hours · On location',
        eventType: 'Half-Day Content',
        features: [
            '30–50 curated photos',
            '2–3 short-form reels',
            'Lifestyle + product coverage',
        ],
    },
    {
        id: 'fullday',
        label: 'FULL-DAY CONTENT',
        price: '$1,400',
        meta: '8 hours · On location',
        eventType: 'Full-Day Content',
        features: [
            '50+ curated photos',
            '2–3 short-form reels',
            'Everything a brand needs in a single day',
        ],
        featured: true,
    },
];

const WEB_SERVICES = [
    {
        id: 'starter',
        label: 'STARTER',
        price: '$1,500',
        eventType: 'Web Development',
        isConsultation: true,
        features: [
            'Template-based site',
            '5–8 pages',
            'Mobile responsive',
            'Vercel deployment',
        ],
    },
    {
        id: 'professional',
        label: 'PROFESSIONAL',
        price: '$3,500',
        eventType: 'Web Development',
        isConsultation: true,
        features: [
            'Custom branding',
            'Dashboard / admin panel',
            'Booking system',
            'SEO optimization',
        ],
        featured: true,
    },
    {
        id: 'agency',
        label: 'AGENCY',
        price: '$6,000+',
        eventType: 'Web Development',
        isConsultation: true,
        features: [
            'Full custom build',
            'CRM integration',
            'Email automation',
            'Payment processing',
        ],
    },
    {
        id: 'maintenance',
        label: 'MONTHLY MAINTENANCE',
        price: '$150–300/mo',
        eventType: 'Retainer (Monthly)',
        isConsultation: true,
        features: [
            'Content updates',
            'Performance monitoring',
            'Priority support',
            'Security patches',
        ],
    },
];

// Standalone video. Previously video existed only as reels bundled into the
// photo content days, so /services/video advertised something that could not
// be booked on its own.
//
// Priced against the Austin market (2026): freelance video runs $600–1,500 for
// a half day and $1,200–3,000 for a full day; Austin single-camera branded
// packages start around $1,400–2,200; agencies charge $1,500–5,000 for
// short-form social alone; retainers of 4–8 videos/month run $1,000–2,500.
// Austin sits 25–40% under LA/NYC for equivalent work. The ladder below enters
// well beneath the agency floor with REEL PACK, then tracks the freelance band
// — the same logic as the $195 Airbnb entry: undercut the mid-market at the
// door, carry the margin further up.
const VIDEO_SERVICES = [
    {
        id: 'reelpack',
        label: 'REEL PACK',
        price: '$450',
        meta: '2 hours · On location',
        eventType: 'Reel Pack',
        features: [
            '3 short-form reels, vertical',
            '5-day delivery',
            'Licensed for paid social',
        ],
    },
    {
        id: 'brandvideo',
        label: 'BRAND VIDEO',
        price: '$1,200',
        meta: '4 hours · On location',
        eventType: 'Brand Video',
        features: [
            'One 60–90s hero video',
            '3 vertical cutdowns for social',
            'Licensed music + captions',
        ],
        featured: true,
    },
    {
        id: 'videoday',
        label: 'VIDEO CONTENT DAY',
        price: '$2,000',
        meta: '8 hours · On location',
        eventType: 'Video Content Day',
        features: [
            'Hero video plus 6–8 reels',
            'Multi-location or multi-setup',
            'A month of social in one day',
        ],
    },
    {
        id: 'reelretainer',
        label: 'MONTHLY REEL RETAINER',
        price: '$900/mo',
        eventType: 'Retainer (Monthly)',
        isConsultation: true,
        features: [
            '4 reels per month',
            'One shoot day, batched',
            'Priority turnaround',
        ],
    },
];

const BUNDLES = [
    {
        id: 'launch',
        label: 'LAUNCH PACKAGE',
        price: '$2,500',
        tagline: 'Launch online, fully equipped.',
        eventType: 'Web Development',
        isConsultation: true,
        features: [
            'Starter website (5–8 pages)',
            'Lifestyle portrait session',
            'Product & space photography for the site',
            'Same-day photo delivery',
        ],
    },
    {
        id: 'brand',
        label: 'BRAND PACKAGE',
        price: '$5,000',
        tagline: 'A complete brand presence, built and shot in one engagement.',
        eventType: 'Brand / Editorial',
        isConsultation: true,
        features: [
            'Professional website with custom branding',
            'Half-day content shoot (4 hrs)',
            'Brand photography + social assets',
            'Short-form reel content',
        ],
        featured: true,
    },
];

const TRAVEL_TIERS = [
    { range: 'Downtown Austin', rate: 'Included' },
    { range: '15–50 mi', rate: '+$50' },
    { range: '50+ mi', rate: '+$100 + mileage' },
    { range: 'Overnight', rate: 'Lodging at cost' },
];

// ── Booking calendar/wizard ───────────────────────────────────────────────────

const WEBDEV_EVENT_TYPES = ['Web Development', 'Retainer (Monthly)'];
const isWebDevEventType = (eventType: string) => WEBDEV_EVENT_TYPES.includes(eventType);

// Mirrors AIRBNB_TIERS in api/booking/index.ts. Display only — the server
// resolves the actual price from the bedroom count it is sent.
const AIRBNB_BEDROOM_OPTIONS = [
    { value: 1, label: 'Studio / 1', price: 195 },
    { value: 2, label: '2 bed', price: 265 },
    { value: 3, label: '3 bed', price: 335 },
    { value: 4, label: '4+ / lux', price: 425 },
];

const EVENT_TYPES = [
    'Portrait Session',
    'Lifestyle Shoot',
    'Airbnb / Short-Term Rental',
    'Real Estate / Multifamily',
    'Event Coverage',
    'Half-Day Content',
    'Full-Day Content',
    'Brand / Editorial',
    'Reel Pack',
    'Brand Video',
    'Video Content Day',
    'Web Development',
    'Retainer (Monthly)',
    'Other',
];

interface TimeSlot { label: string; start: string; end: string; display: string }

const TIME_SLOTS: TimeSlot[] = [
    { label: 'Early Morning', start: '06:00', end: '08:00', display: '6AM – 8AM' },
    { label: 'Morning',   start: '09:00', end: '12:00', display: '9AM – 12PM' },
    { label: 'Afternoon', start: '12:00', end: '17:00', display: '12PM – 5PM' },
    { label: 'Evening',   start: '17:00', end: '20:00', display: '5PM – 8PM' },
    { label: 'Night',     start: '20:00', end: '23:00', display: '8PM – 11PM' },
];

// A booked shoot reserves its window plus travel time either side, so a slot
// that merely fails to overlap is not necessarily free. The arithmetic lives in
// lib/booking-buffer.ts and is shared with the server, which enforces it.
function isSlotBlocked(slot: TimeSlot, taken: Array<{ start_time: string | null; end_time: string | null }>): boolean {
    return conflictsWithBuffer(slot.start, slot.end, taken);
}

interface FormData {
    name: string;
    business_name: string;
    email: string;
    phone: string;
    event_type: string;
    event_date: string;
    start_time: string;
    end_time: string;
    location: string;
    notes: string;
    retainer: boolean;
    bedrooms?: number;
    access_notes?: string;
}

const EMPTY_FORM: FormData = {
    name: '',
    business_name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: '',
    start_time: '',
    end_time: '',
    location: '',
    notes: '',
    retainer: false,
};

const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BookingCalendar: React.FC<{
    selected: string;
    blockedDates: string[];
    onSelect: (date: string) => void;
}> = ({ selected, blockedDates, onSelect }) => {
    const today = new Date();
    const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

    const days = useMemo(() => {
        const firstDay = new Date(cursor.year, cursor.month, 1).getDay();
        const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
        const cells: (number | null)[] = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return cells;
    }, [cursor]);

    const fmt = (d: number) => {
        const mm = String(cursor.month + 1).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        return `${cursor.year}-${mm}-${dd}`;
    };

    const isPast = (d: number) => {
        const dt = new Date(cursor.year, cursor.month, d);
        dt.setHours(0, 0, 0, 0);
        const t = new Date(); t.setHours(0, 0, 0, 0);
        return dt < t;
    };

    const prev = () => setCursor(c => {
        const m = c.month === 0 ? 11 : c.month - 1;
        const y = c.month === 0 ? c.year - 1 : c.year;
        return { year: y, month: m };
    });
    const next = () => setCursor(c => {
        const m = c.month === 11 ? 0 : c.month + 1;
        const y = c.month === 11 ? c.year + 1 : c.year;
        return { year: y, month: m };
    });

    return (
        <div className="w-full">
            <div className="flex items-center justify-between mb-4">
                <button onClick={prev} className="p-1 text-white/40 hover:text-white transition-colors">
                    <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-xs font-black uppercase tracking-[0.2em] text-white">
                    {MONTH_NAMES[cursor.month]} {cursor.year}
                </span>
                <button onClick={next} className="p-1 text-white/40 hover:text-white transition-colors">
                    <ChevronRightIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="grid grid-cols-7 mb-1">
                {DAY_NAMES.map(d => (
                    <div key={d} className="text-center text-[9px] font-black uppercase tracking-widest text-white/20 py-1">
                        {d}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
                {days.map((d, i) => {
                    if (!d) return <div key={`empty-${i}`} />;
                    const dateStr = fmt(d);
                    const past = isPast(d);
                    const blocked = blockedDates.includes(dateStr);
                    const isSel = dateStr === selected;
                    const disabled = past || blocked;
                    return (
                        <button
                            key={dateStr}
                            disabled={disabled}
                            onClick={() => onSelect(dateStr)}
                            className={`
                                aspect-square flex items-center justify-center text-[11px] font-bold transition-all
                                ${isSel
                                    ? 'bg-white text-black'
                                    : disabled
                                        ? 'text-white/15 cursor-not-allowed'
                                        : 'text-white/60 hover:bg-white/10 hover:text-white'}
                            `}
                        >
                            {d}
                        </button>
                    );
                })}
            </div>

            <div className="mt-3 flex items-center gap-4 text-[9px] text-white/30 font-bold uppercase tracking-widest">
                <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-white/15 inline-block" />
                    Unavailable
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-white inline-block" />
                    Selected
                </span>
            </div>
        </div>
    );
};

// ── Service card components ───────────────────────────────────────────────────

const ServiceCard: React.FC<{
    label: string;
    price: string;
    meta?: string;
    features: string[];
    featured?: boolean;
    isConsultation?: boolean;
    onBook?: () => void;
}> = ({ label, price, meta, features, featured, isConsultation, onBook }) => {
    const base = featured
        ? 'bg-white text-black'
        : 'bg-white/[0.04] text-white';
    const priceColor = featured ? 'text-black' : 'text-white';
    const metaColor = featured ? 'text-black/50' : 'text-white/40';
    const featureColor = featured ? 'text-black/60' : 'text-white/50';
    const dotColor = featured ? 'bg-black/20' : 'bg-white/20';

    return (
        <div className={`${base} p-6 flex flex-col`}>
            <p className="text-[9px] font-black uppercase tracking-[0.3em] mb-3 opacity-60">{label}</p>
            <p className={`text-3xl font-black ${priceColor} mb-1`}>{price}</p>
            {meta && <p className={`text-[10px] font-bold uppercase tracking-wider ${metaColor} mb-5`}>{meta}</p>}
            <ul className="space-y-2 mt-auto">
                {features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                        <span className={`w-1 h-1 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
                        <span className={`text-xs leading-relaxed ${featureColor}`}>{f}</span>
                    </li>
                ))}
            </ul>
            {onBook && (
                <button
                    onClick={onBook}
                    className={`mt-6 w-full py-3 text-[11px] font-black uppercase tracking-widest transition-colors ${
                        featured
                            ? 'bg-black text-white hover:bg-black/80'
                            : 'bg-white text-black hover:bg-white/90'
                    }`}
                >
                    {isConsultation ? 'Book a Free Consultation' : 'Book'}
                </button>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * `focus` swaps the hero's heading and opening line so each service URL leads
 * with what that visitor searched for. Everything below the hero is unchanged —
 * a host who came for listing photography can still see the web packages.
 *
 * The point is that the three URLs are not the same document. Identical pages
 * on three addresses get collapsed to one in search, and the two Google
 * discards are the ones it decides are duplicates — not the one you'd choose.
 */
type ServiceFocus = 'airbnb' | 'realestate' | 'web' | 'video';

/** The service URLs, in the order they read as a set. Keys match ServiceFocus,
 *  plus 'all' for the hub at /services. Mirrors SERVICE_PAGES in Gallery.tsx
 *  and the services entries in scripts/pre-render-core-pages.ts. */
const SERVICE_LINKS: { path: string; label: string; key: ServiceFocus | 'all' }[] = [
    { path: '/services', label: 'All Services', key: 'all' },
    { path: '/services/airbnb-photography', label: 'Airbnb', key: 'airbnb' },
    { path: '/services/real-estate-photography', label: 'Real Estate', key: 'realestate' },
    { path: '/services/web-design', label: 'Web Design', key: 'web' },
    { path: '/services/video', label: 'Video', key: 'video' },
];

const FOCUS_HERO: Record<ServiceFocus, { h1: React.ReactNode; copy: string }> = {
    airbnb: {
        h1: <>AIRBNB<br />PHOTOGRAPHY</>,
        copy: 'Listing photography for Austin short-term rentals. 25–35 edited photos, delivered in 24–72 hours, priced from $195.',
    },
    realestate: {
        h1: <>REAL ESTATE<br />PHOTOGRAPHY</>,
        copy: 'Leasing and listing photography for Austin apartment communities, property managers and agents. Model units from $225, full property packages at $850, portfolio retainers from $1,600/mo.',
    },
    web: {
        h1: <>WEB<br />DESIGN</>,
        copy: 'Websites for Austin small businesses, artists, and brands. From a five-page starter site to a full custom build with booking and payments, from $1,500.',
    },
    video: {
        h1: <>VIDEO<br />CONTENT</>,
        copy: 'Short-form video for brands in Austin. Reel packs from $450, brand films from $1,200, and full video content days \u2014 plus reels shot alongside stills on any content day.',
    },
};

const BookingPage: React.FC<{ focus?: ServiceFocus }> = ({ focus }) => {
    const scheduleRef = useRef<HTMLDivElement>(null);

    // Booking wizard state
    const [form, setForm] = useState<FormData>(EMPTY_FORM);
    const [blockedDates, setBlockedDates] = useState<string[]>([]);
    const [step, setStep] = useState<'calendar' | 'details' | 'success'>('calendar');
    const [detailsStep, setDetailsStep] = useState(0);
    const TOTAL_DETAILS_STEPS = 5;
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Turnstile (TURNSTILE RULE): single-use, so remounted after every attempt.
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileKey, setTurnstileKey] = useState(0);
    const [notifyDebug, setNotifyDebug] = useState<string | null>(null);
    // Brand assets picked on the form, sent through this booking's upload link
    // once it saves (the server returns the link with the booking).
    const [brandFiles, setBrandFiles] = useState<File[]>([]);
    const [brandLink, setBrandLink] = useState<string | null>(null);
    const [brandStatus, setBrandStatus] = useState<{ state: 'idle' | 'sending' | 'done' | 'error'; received?: number; error?: string }>({ state: 'idle' });
    const [contextOpen, setContextOpen] = useState(false);
    const [bookedSlots, setBookedSlots] = useState<Array<{ start_time: string | null; end_time: string | null }>>([]);
    const [conflictingEvents, setConflictingEvents] = useState<Array<{ id: string; title: string; location: string | null }>>([]);
    // Slot labels bookable on the selected date. null = no restriction on file,
    // so every slot stays open (the behaviour before per-date windows existed).
    const [allowedSlots, setAllowedSlots] = useState<string[] | null>(null);
    const wizardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!form.event_date) {
            setBookedSlots([]); setConflictingEvents([]); setAllowedSlots(null); return;
        }
        fetch(`/api/booking?action=slots&date=${form.event_date}`)
            .then(r => r.ok ? r.json() : { slots: [], events: [], allowedSlots: null })
            .then(d => {
                setBookedSlots(d.slots || []);
                setConflictingEvents(d.events || []);
                setAllowedSlots(Array.isArray(d.allowedSlots) ? d.allowedSlots : null);
            })
            .catch(() => { setBookedSlots([]); setConflictingEvents([]); setAllowedSlots(null); });
    }, [form.event_date]);

    useEffect(() => {
        requestAnimationFrame(() => {
            if (step === 'details' && wizardRef.current) {
                const navVar = getComputedStyle(document.documentElement).getPropertyValue('--nav-height').trim();
                const navH = navVar ? parseInt(navVar) : 64;
                const tabsH = 56;
                const rect = wizardRef.current.getBoundingClientRect();
                const top = window.pageYOffset + rect.top - navH - tabsH - 8;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        });
    }, [detailsStep, step]);

    const canProceedDetails = (): boolean => {
        switch (detailsStep) {
            case 0: return form.name.trim().length > 0;
            case 1: return form.email.trim().length > 0 && form.email.includes('@');
            case 2:
                if (form.event_type.trim().length === 0) return false;
                // An Airbnb booking without a bedroom count cannot be priced,
                // so it would silently fall back to a manual quote.
                if (form.event_type === 'Airbnb / Short-Term Rental') return !!form.bedrooms;
                return true;
            case 3: return true;
            case 4: return true;
            default: return false;
        }
    };

    useEffect(() => {
        fetch('/api/booking?action=availability')
            .then(r => r.json())
            .then(d => setBlockedDates(d.blockedDates || []))
            .catch(() => {});
    }, []);

    const set = (field: keyof FormData, value: any) =>
        setForm(prev => ({ ...prev, [field]: value }));

    // Deep link from a client email CTA:
    //   /?view=booking&service=airbnb&promo=AIRBNB10
    // `service` preselects the event type and jumps to the scheduler, so a
    // repeat client books the thing they already bought instead of landing on
    // a generic service list. `promo` is recorded on the booking — there is no
    // automated discount engine for bookings, so the code travels in the notes
    // and is honoured when the invoice is raised.
    const [promoCode, setPromoCode] = useState('');
    // A client we already know — their name and email ride in the link we sent
    // them, so the wizard's identity steps are pure friction. Express mode asks
    // only for what we cannot know: when, where, and how to get in.
    const [expressMode, setExpressMode] = useState(false);
    // Arrived via ?service= — the visitor asked for the calendar, not the pitch.
    const [deepLinked, setDeepLinked] = useState(false);
    // ?client=<crm id> — a personal link. The modal resolves them from the CRM.
    const [expressClientId, setExpressClientId] = useState<string | null>(null);
    const [expressOpen, setExpressOpen] = useState(false);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const svc = (params.get('service') || '').toLowerCase();
        if (svc) {
            const match = [...PHOTO_SERVICES, ...VIDEO_SERVICES, ...WEB_SERVICES, ...BUNDLES]
                .find(s => s.id === svc);
            if (match) {
                setForm(prev => ({ ...prev, event_type: match.eventType }));
                // No scrolling. The pitch above the scheduler is hidden for a
                // deep link, so the calendar is already the top of the page —
                // nothing to travel to, nothing to jump.
                setDeepLinked(true);
            }
        }

        const pName = (params.get('name') || params.get('n') || '').trim();
        const pEmail = (params.get('email') || params.get('e') || '').trim();
        const pBiz = (params.get('business') || params.get('b') || '').trim();
        if (pName && pEmail.includes('@')) {
            setForm(prev => ({
                ...prev,
                name: pName,
                email: pEmail,
                business_name: pBiz || prev.business_name,
            }));
            setExpressMode(true);
        }

        const cid = (params.get('client') || '').trim();
        if (/^[0-9a-f-]{36}$/i.test(cid)) {
            setExpressClientId(cid);
            setExpressOpen(true);
        }

        const beds = Number(params.get('beds') || params.get('bedrooms'));
        if (Number.isFinite(beds) && beds > 0) {
            setForm(prev => ({ ...prev, bedrooms: beds }));
        }

        const promo = (params.get('promo') || '').trim();
        if (promo && /^[A-Za-z0-9-]{3,24}$/.test(promo)) {
            const code = promo.toUpperCase();
            setPromoCode(code);
            setForm(prev => ({
                ...prev,
                notes: prev.notes ? `${prev.notes}\n\nPromo code: ${code}` : `Promo code: ${code}`,
            }));
        }
    }, []);

    const handleDateSelect = (date: string) => {
        set('event_date', date);
        setStep('details');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (turnstileRequired() && !turnstileToken) {
            setError('Still running the security check — give it a second and try again.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch('/api/booking?action=request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    notes: [form.notes, form.access_notes && `Access: ${form.access_notes}`]
                        .filter(Boolean).join('\n\n'),
                    retainer: form.event_type === 'Retainer (Monthly)',
                    turnstileToken,
                }),
            });
            const rawText = await res.text();
            let data: any = null;
            try {
                data = rawText ? JSON.parse(rawText) : null;
            } catch {
                data = { error: `Server returned non-JSON (${res.status}): ${rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 180)}` };
            }
            if (!res.ok) throw new Error(data?.error || `Submission failed (${res.status})`);
            if (data?.notify && data.notify.sent === false && data.notify.error) {
                setNotifyDebug(data.notify.error);
            } else {
                setNotifyDebug(null);
            }
            const link: { token: string; url: string } | null = data?.brandAssets || null;
            setBrandLink(link?.url || null);
            setStep('success');
            // The booking is saved; the files ride its own upload link. A
            // failure here leaves the booking intact and the link on screen.
            if (brandFiles.length) {
                if (!link) {
                    setBrandStatus({ state: 'error', error: 'Your booking is in, but the upload link could not be made. Reply to the confirmation email with your files.' });
                } else {
                    setBrandStatus({ state: 'sending' });
                    sendFilesToUploadLink(link.token, brandFiles, { note: 'Sent with the booking request.' })
                        .then(r => { setBrandStatus({ state: 'done', received: r.received }); setBrandFiles([]); })
                        .catch((e: any) => setBrandStatus({ state: 'error', error: e?.message || 'Upload failed.' }));
                }
            }
        } catch (err: any) {
            setError(err?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
            setTurnstileToken(null);
            setTurnstileKey(k => k + 1);
        }
    };

    const formatDate = (d: string) => {
        if (!d) return '';
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        });
    };

    const scrollToSchedule = () => {
        scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleBookService = (eventType: string) => {
        set('event_type', eventType);
        scrollToSchedule();
    };

    return (
        <div className="min-h-screen bg-black text-white">

            {expressClientId && expressOpen && (
                <ExpressBookingModal
                    clientId={expressClientId}
                    eventType={form.event_type || 'Airbnb / Short-Term Rental'}
                    promoCode={promoCode || undefined}
                    discountPct={promoCode ? 10 : 0}
                    initialBedrooms={form.bedrooms}
                    onClose={() => setExpressOpen(false)}
                />
            )}

            {/* Everything above the scheduler is the pitch. A client who followed a
                booking link from an email has already been sold — showing them the
                pitch first means the page must scroll to reach the calendar, and
                any scroll on arrival reads as a jump. Hide it so the scheduler is
                simply the top of the page. */}
            {!deepLinked && (<>
            {/* ── Hero ──────────────────────────────────────────────── */}
            <div className="px-4 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
                <div className="max-w-5xl mx-auto">
                    <p className="text-[9px] font-black uppercase tracking-[0.5em] text-white/30 mb-4">
                        THE LOST+UNFOUNDS
                    </p>
                    <h1 className="text-5xl md:text-8xl font-black uppercase tracking-tight leading-none text-white mb-6">
                        {focus ? FOCUS_HERO[focus].h1 : <>CREATIVE<br />AGENCY</>}
                    </h1>
                    <p className="text-white/50 text-base md:text-lg max-w-xl leading-relaxed mb-10">
                        {focus
                            ? FOCUS_HERO[focus].copy
                            : 'Photography and web development for brands, artists, and businesses in Austin. Authentic moments, fast delivery, real results.'}
                    </p>
                    <button
                        onClick={scrollToSchedule}
                        className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 text-[11px] font-black uppercase tracking-widest hover:bg-white/90 transition-colors"
                    >
                        Schedule a Session
                        <ArrowLongRightIcon className="w-4 h-4" />
                    </button>

                    {/* The five service URLs exist so each can rank for its own
                        search, but nothing on the site linked to any of them —
                        Ahrefs reported all five as orphan pages, which is a page
                        Google finds in the sitemap and nowhere else. Every offer
                        now links the others, so the set holds itself up. The
                        current page is plain text rather than a link to itself. */}
                    <nav aria-label="Services" className="mt-10 flex flex-wrap gap-x-5 gap-y-2 text-[10px] font-black uppercase tracking-widest">
                        {SERVICE_LINKS.map(({ path, label, key }) => (
                            key === (focus ?? 'all') ? (
                                <span key={path} className="text-white/70">{label}</span>
                            ) : (
                                <Link key={path} to={path} className="text-white/30 hover:text-white transition-colors">
                                    {label}
                                </Link>
                            )
                        ))}
                    </nav>
                </div>
            </div>

            {/* ── Photography Services ──────────────────────────────── */}
            <div className="px-4 md:px-8 py-16 md:py-20">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4 mb-10">
                        <CameraIcon className="w-5 h-5 text-white/30 flex-shrink-0" />
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/30 mb-1">01</p>
                            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                                PHOTOGRAPHY
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {PHOTO_SERVICES.map(s => (
                            <ServiceCard key={s.id} {...s} onBook={() => handleBookService(s.eventType)} />
                        ))}
                    </div>

                    {/* Travel pricing */}
                    <div className="mt-6 bg-white/[0.03] px-6 py-5">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPinIcon className="w-4 h-4 text-white/30" />
                            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white/40">Travel</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {TRAVEL_TIERS.map(t => (
                                <div key={t.range}>
                                    <p className="text-[10px] font-black uppercase tracking-wider text-white/30 mb-1">{t.range}</p>
                                    <p className="text-sm font-black text-white">{t.rate}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Photography philosophy */}
                    <div className="mt-6 bg-white/[0.03] px-6 py-5 flex items-start gap-4">
                        <BoltIcon className="w-5 h-5 text-white/30 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-black uppercase tracking-wider text-white mb-1">
                                Authentic Moments, Delivered Fast
                            </p>
                            <p className="text-white/40 text-xs leading-relaxed max-w-2xl">
                                Candid-first direction and a{' '}
                                <span className="text-white font-bold">fast turnaround</span> —
                                real moments, not posed briefs. We match each shoot to the right
                                photographer and the right kit, so the work fits the space instead
                                of the other way around. Tell us what you need and we'll scope it
                                with you.
                            </p>
                        </div>
                    </div>
                </div>
            </div>            {/* ── Video Services ────────────────────────────────────── */}
            <div className="px-4 md:px-8 py-16 md:py-20">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4 mb-10">
                        <VideoCameraIcon className="w-5 h-5 text-white/30 flex-shrink-0" />
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/30 mb-1">02</p>
                            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                                VIDEO
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {VIDEO_SERVICES.map(s => (
                            <ServiceCard key={s.id} {...s} onBook={() => handleBookService(s.eventType)} />
                        ))}
                    </div>
                </div>
            </div>



            {/* ── Web Development ───────────────────────────────────── */}
            <div className="px-4 md:px-8 py-16 md:py-20">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4 mb-10">
                        <ComputerDesktopIcon className="w-5 h-5 text-white/30 flex-shrink-0" />
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/30 mb-1">03</p>
                            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                                WEB DEVELOPMENT
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {WEB_SERVICES.map(s => (
                            <ServiceCard key={s.id} {...s} onBook={() => handleBookService(s.eventType)} />
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Bundled Packages ──────────────────────────────────── */}
            <div className="px-4 md:px-8 py-16 md:py-20">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4 mb-10">
                        <RocketLaunchIcon className="w-5 h-5 text-white/30 flex-shrink-0" />
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/30 mb-1">04</p>
                            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                                BUNDLED PACKAGES
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {BUNDLES.map(b => (
                            <div
                                key={b.id}
                                className={b.featured ? 'bg-white text-black p-6 flex flex-col' : 'bg-white/[0.04] text-white p-6 flex flex-col'}
                            >
                                <p className={`text-[9px] font-black uppercase tracking-[0.3em] mb-3 ${b.featured ? 'text-black/50' : 'text-white/40'}`}>
                                    {b.label}
                                </p>
                                <p className={`text-3xl font-black mb-1 ${b.featured ? 'text-black' : 'text-white'}`}>{b.price}</p>
                                <p className={`text-xs leading-relaxed mb-5 ${b.featured ? 'text-black/50' : 'text-white/40'}`}>
                                    {b.tagline}
                                </p>
                                <ul className="space-y-2 mt-auto">
                                    {b.features.map(f => (
                                        <li key={f} className="flex items-start gap-2">
                                            <span className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${b.featured ? 'bg-black/20' : 'bg-white/20'}`} />
                                            <span className={`text-xs leading-relaxed ${b.featured ? 'text-black/60' : 'text-white/50'}`}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => handleBookService(b.eventType)}
                                    className={`mt-6 w-full py-3 text-[11px] font-black uppercase tracking-widest transition-colors ${
                                        b.featured
                                            ? 'bg-black text-white hover:bg-black/80'
                                            : 'bg-white text-black hover:bg-white/90'
                                    }`}
                                >
                                    {b.isConsultation ? 'Book a Free Consultation' : 'Book This Package'}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 bg-white/[0.03] px-6 py-4 flex items-center gap-3">
                        <ClockIcon className="w-4 h-4 text-white/30 flex-shrink-0" />
                        <p className="text-white/40 text-xs">
                            All packages require a{' '}
                            <span className="text-white font-bold">50% non-refundable retainer</span>{' '}
                            to hold the date. Balance due before the session.
                            Payment via Stripe or Bitcoin.
                        </p>
                    </div>
                </div>
            </div>

            </>)}

            {/* ── Schedule a Session ────────────────────────────────── */}
            <div ref={scheduleRef} className="px-4 md:px-8 pt-16 md:pt-20 pb-32">
                <div className="max-w-5xl mx-auto">

                    <div className="mb-12">
                        <p className="text-[9px] font-black uppercase tracking-[0.5em] text-white/30 mb-3">
                            READY TO START
                        </p>
                        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white mb-3">
                            SCHEDULE A SESSION
                        </h2>
                        <p className="text-white/40 text-sm max-w-md">
                            Pick a date and we'll reach out within 24 hours to confirm scope and send a contract.
                        </p>
                        {promoCode && (
                            <div className="mt-6 bg-white/10 px-5 py-4 max-w-md">
                                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/40 mb-2">
                                    DISCOUNT APPLIED
                                </p>
                                <p className="text-white text-sm font-bold">
                                    10% off this booking &middot; {promoCode}
                                </p>
                                <p className="text-white/40 text-xs mt-2">
                                    We'll apply it to your invoice — nothing else to enter.
                                </p>
                            </div>
                        )}
                    </div>

                    <AnimatePresence mode="wait">

                        {/* Calendar step */}
                        {step === 'calendar' && (
                            <motion.div
                                key="calendar"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16 }}
                                className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24"
                            >
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/40 mb-6">
                                        01 — Pick a Date
                                    </p>
                                    <BookingCalendar
                                        selected={form.event_date}
                                        blockedDates={blockedDates}
                                        onSelect={handleDateSelect}
                                    />
                                </div>

                                <div className="space-y-4 md:pt-10">
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/40">
                                        Good to Know
                                    </p>
                                    <div className="bg-white/[0.03] p-5">
                                        <p className="text-sm font-black uppercase tracking-wider text-white mb-1">Response Time</p>
                                        <p className="text-white/50 text-xs leading-relaxed">
                                            All requests receive a reply within 24 hours. Selecting a date here
                                            holds a soft reservation while we confirm scope together.
                                        </p>
                                    </div>
                                    <div className="bg-white/[0.03] p-5">
                                        <p className="text-sm font-black uppercase tracking-wider text-white mb-1">Photography</p>
                                        <p className="text-white/40 text-xs leading-relaxed">
                                            Candid-first, lifestyle direction. Real moments, not posed briefs.
                                            Contact us and we'll match the shoot to the right photographer.
                                        </p>
                                    </div>
                                    <div className="bg-white/[0.03] p-5">
                                        <p className="text-sm font-black uppercase tracking-wider text-white mb-1">Web Projects</p>
                                        <p className="text-white/40 text-xs leading-relaxed">
                                            Initial call to scope the build, then a fixed-price proposal.
                                            Select "Web Development" in the service step.
                                        </p>
                                    </div>
                                    <div className="bg-white text-black p-5">
                                        <p className="text-sm font-black uppercase tracking-wider mb-1">50% Retainer Required</p>
                                        <p className="text-black/60 text-xs leading-relaxed">
                                            A 50% retainer holds your date. Balance is due before the session.
                                            Contract and payment link arrive after we confirm scope.
                                        </p>
                                    </div>
                                    <div className="bg-white/[0.03] p-5">
                                        <p className="text-sm font-black uppercase tracking-wider text-white mb-1">Cancellation &amp; Rescheduling</p>
                                        <p className="text-white/40 text-xs leading-relaxed">
                                            The retainer is non-refundable — it pays for the date being held.
                                            Cancel more than 14 days out and nothing further is owed; inside 14 days,
                                            25% of the balance is owed; inside 48 hours or a no-show, the full amount is owed.
                                            One free reschedule with 48+ hours' notice, within 90 days and subject to availability.
                                            If we cancel, everything you've paid comes back in full.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Details wizard */}
                        {step === 'details' && (
                            <motion.div
                                key="details"
                                ref={wizardRef}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16 }}
                                className="max-w-md scroll-mt-4"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <button
                                        onClick={() => setContextOpen(v => !v)}
                                        className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 transition-colors px-2.5 py-1.5"
                                    >
                                        <CalendarIcon className="w-3.5 h-3.5 text-white/60" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/60">
                                            {new Date(form.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                    </button>
                                    {!expressMode && (
                                        <div className="flex-1 flex items-center gap-1">
                                            {Array.from({ length: TOTAL_DETAILS_STEPS }).map((_, s) => (
                                                <div key={s} className={`h-1 flex-1 ${detailsStep >= s ? 'bg-white' : 'bg-white/20'}`} />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {contextOpen && (
                                    <div className="bg-white/5 p-3 mb-6 flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-white/40 mb-1">Selected Date</p>
                                            <p className="text-sm font-bold text-white">{formatDate(form.event_date)}</p>
                                        </div>
                                        <button
                                            onClick={() => { setStep('calendar'); setDetailsStep(0); setError(''); setContextOpen(false); }}
                                            className="text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white transition-colors px-3 py-2 bg-white/5 hover:bg-white/10 whitespace-nowrap"
                                        >
                                            Change date
                                        </button>
                                    </div>
                                )}


                                {expressMode && (
                                    <div>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                <CameraIcon className="w-5 h-5 text-white/60" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">Confirm your shoot</h3>
                                                <p className="text-white/40 text-sm">
                                                    {form.name ? `${form.name} — ` : ''}we have the rest on file
                                                </p>
                                            </div>
                                        </div>

                                        <div className="bg-white/5 p-4 mb-6">
                                            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-white/40 mb-1">Booking</p>
                                            <p className="text-sm font-bold text-white">
                                                {form.event_type}
                                                {form.bedrooms ? ` · ${AIRBNB_BEDROOM_OPTIONS.find(o => o.value === form.bedrooms)?.label}` : ''}
                                            </p>
                                            <p className="text-white/40 text-xs mt-1">{formatDate(form.event_date)}</p>
                                        </div>

                                        <p className="text-white text-sm font-bold mb-1">Pick a time</p>
                                        <p className="text-white/40 text-xs mb-3">These are the windows we can cover.</p>
                                        <div className="grid grid-cols-1 gap-2 mb-6">
                                            {TIME_SLOTS
                                                .filter(slot => !allowedSlots || allowedSlots.includes(slot.label))
                                                .filter(slot => !isSlotBlocked(slot, bookedSlots))
                                                .map(slot => {
                                                    const selected = form.start_time === slot.start && form.end_time === slot.end;
                                                    return (
                                                        <button
                                                            key={slot.label}
                                                            type="button"
                                                            onClick={() => { set('start_time', slot.start); set('end_time', slot.end); }}
                                                            className={`w-full px-4 py-3 text-left transition-colors rounded-none flex items-center justify-between ${selected ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}
                                                        >
                                                            <span className="font-black uppercase tracking-wider text-sm">{slot.label}</span>
                                                            <span className={`text-[10px] font-mono ${selected ? 'text-black/60' : 'text-white/40'}`}>{slot.display}</span>
                                                        </button>
                                                    );
                                                })}
                                        </div>

                                        <p className="text-white text-sm font-bold mb-1">Where is it?</p>
                                        <p className="text-white/40 text-xs mb-3">Full address of the unit.</p>
                                        <input
                                            type="text"
                                            value={form.location}
                                            onChange={e => set('location', e.target.value)}
                                            placeholder="Street address, unit number, city"
                                            className="w-full bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:bg-white/10 transition-colors rounded-none mb-6"
                                        />

                                        <p className="text-white text-sm font-bold mb-1">How do we get in?</p>
                                        <p className="text-white/40 text-xs mb-3">
                                            Lockbox code, gate code, parking, who to call — anything the photographer needs on the day.
                                        </p>
                                        <textarea
                                            value={form.access_notes || ''}
                                            onChange={e => set('access_notes', e.target.value)}
                                            rows={4}
                                            placeholder="e.g. Gate code 4821, unit 204, lockbox on the door — code 1105. Parking in visitor spots out front."
                                            className="w-full bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:bg-white/10 transition-colors rounded-none mb-6 resize-none"
                                        />

                                        <BrandAssetPicker files={brandFiles} onChange={setBrandFiles} onError={setError} />

                                        <TurnstileField
                                            action="booking"
                                            resetKey={turnstileKey}
                                            onToken={setTurnstileToken}
                                            onFailed={() => setError('The security check failed to load. Refresh the page and try again.')}
                                        />
                                        {error && (
                                            <p className="text-white/80 text-sm mb-4">{error}</p>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleSubmit as any}
                                            disabled={submitting || !form.start_time || !form.location.trim()}
                                            className="w-full px-6 py-4 bg-white text-black font-black uppercase tracking-widest text-sm hover:bg-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? 'Generating…' : 'Generate invoice'}
                                        </button>
                                        <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest text-center mt-3">
                                            Your invoice and deposit link arrive by email
                                        </p>
                                    </div>
                                )}

                                {!expressMode && detailsStep === 0 && (
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                <UserIcon className="w-5 h-5 text-white/60" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">What's your name?</h3>
                                                <p className="text-white/40 text-sm">So I know who's reaching out</p>
                                            </div>
                                        </div>
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Full name"
                                            value={form.name}
                                            onChange={e => set('name', e.target.value)}
                                            className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors mb-3"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Business / Organization (optional)"
                                            value={form.business_name}
                                            onChange={e => set('business_name', e.target.value)}
                                            className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors"
                                        />
                                    </div>
                                )}

                                {!expressMode && detailsStep === 1 && (
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                <EnvelopeIcon className="w-5 h-5 text-white/60" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">Your email</h3>
                                                <p className="text-white/40 text-sm">I'll reach out here</p>
                                            </div>
                                        </div>
                                        <input
                                            type="email"
                                            autoFocus
                                            placeholder="your@email.com"
                                            value={form.email}
                                            onChange={e => set('email', e.target.value)}
                                            className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors mb-3"
                                        />
                                        <input
                                            type="tel"
                                            placeholder="Phone (optional)"
                                            value={form.phone}
                                            onChange={e => set('phone', e.target.value)}
                                            className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors"
                                        />
                                    </div>
                                )}

                                {!expressMode && detailsStep === 2 && (
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                <CalendarIcon className="w-5 h-5 text-white/60" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">What service?</h3>
                                                <p className="text-white/40 text-sm">Select the closest match</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {EVENT_TYPES.map(t => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => set('event_type', t)}
                                                    className={`w-full px-3 py-2.5 text-[11px] text-center uppercase tracking-wider font-bold transition-colors rounded-none leading-tight ${form.event_type === t ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                        {form.event_type === 'Airbnb / Short-Term Rental' && (
                                            <div className="mt-6">
                                                <p className="text-white text-sm font-bold mb-1">How many bedrooms?</p>
                                                <p className="text-white/40 text-xs mb-3">
                                                    Sets your rate — we'll send the invoice as soon as you book.
                                                </p>
                                                <div className="grid grid-cols-4 gap-2">
                                                    {AIRBNB_BEDROOM_OPTIONS.map(o => (
                                                        <button
                                                            key={o.value}
                                                            type="button"
                                                            onClick={() => set('bedrooms', o.value)}
                                                            className={`px-3 py-3 text-center transition-colors rounded-none ${form.bedrooms === o.value ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}
                                                        >
                                                            <span className="block text-[11px] font-black uppercase tracking-wider leading-tight">{o.label}</span>
                                                            <span className={`block text-[10px] font-mono mt-1 ${form.bedrooms === o.value ? 'text-black/60' : 'text-white/40'}`}>${o.price}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!expressMode && detailsStep === 3 && (
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                <ClockIcon className="w-5 h-5 text-white/60" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">What time works?</h3>
                                                <p className="text-white/40 text-sm">Pick a window — we'll refine when we talk</p>
                                            </div>
                                        </div>
                                        {conflictingEvents.length > 0 && (
                                            <div className="bg-yellow-500/10 text-yellow-300 p-3 mb-3 text-[11px] leading-relaxed">
                                                <p className="font-black uppercase tracking-widest mb-1 text-[9px]">Already scheduled that day</p>
                                                {conflictingEvents.map(e => (
                                                    <p key={e.id}>{e.title}{e.location ? ` · ${e.location}` : ''}</p>
                                                ))}
                                                <p className="mt-2 text-yellow-300/70">
                                                    Pick a non-overlapping window, or select Flexible and we'll work it out.
                                                </p>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 gap-2">
                                            {TIME_SLOTS
                                                .filter(slot => !allowedSlots || allowedSlots.includes(slot.label))
                                                .filter(slot => !isSlotBlocked(slot, bookedSlots))
                                                .map(slot => {
                                                const selected = form.start_time === slot.start && form.end_time === slot.end;
                                                return (
                                                    <button
                                                        key={slot.label}
                                                        type="button"
                                                        onClick={() => { set('start_time', slot.start); set('end_time', slot.end); }}
                                                        className={`w-full px-4 py-3 text-left transition-colors rounded-none flex items-center justify-between ${selected ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}
                                                    >
                                                        <span className="font-black uppercase tracking-wider text-sm">{slot.label}</span>
                                                        <span className={`text-[10px] font-mono ${selected ? 'text-black/60' : 'text-white/40'}`}>{slot.display}</span>
                                                    </button>
                                                );
                                            })}
                                            <button
                                                type="button"
                                                onClick={() => { set('start_time', ''); set('end_time', ''); }}
                                                className={`w-full px-4 py-3 text-left transition-colors rounded-none flex items-center justify-between ${!form.start_time && !form.end_time ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}
                                            >
                                                <span className="font-black uppercase tracking-wider text-sm">Flexible</span>
                                                <span className={`text-[10px] font-mono ${!form.start_time && !form.end_time ? 'text-black/60' : 'text-white/40'}`}>LET'S TALK</span>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!expressMode && detailsStep === 4 && (
                                    isWebDevEventType(form.event_type) ? (
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                    <ComputerDesktopIcon className="w-5 h-5 text-white/60" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold text-white">Tell me about the project</h3>
                                                    <p className="text-white/40 text-sm">Website goals, and any current problems</p>
                                                </div>
                                            </div>
                                            <input
                                                type="text"
                                                autoFocus
                                                placeholder="Current website (if any) — or 'Don't have one yet'"
                                                value={form.location}
                                                onChange={e => set('location', e.target.value)}
                                                className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors mb-3"
                                            />
                                            <textarea
                                                rows={3}
                                                placeholder="What are you trying to build? Any problems with your current site? Goals, must-have features, timeline…"
                                                value={form.notes}
                                                onChange={e => set('notes', e.target.value)}
                                                className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors resize-none"
                                            />
                                            <div className="bg-white/[0.04] p-3 mt-4">
                                                <p className="text-white/60 text-[11px] leading-relaxed">
                                                    Submitting holds this time for a{' '}
                                                    <span className="text-white font-bold">free consultation call</span>{' '}
                                                    — no payment required. We'll scope the project together and follow up with a fixed-price proposal.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                                                    <MapPinIcon className="w-5 h-5 text-white/60" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold text-white">Where & anything else?</h3>
                                                    <p className="text-white/40 text-sm">Venue + any details I should know</p>
                                                </div>
                                            </div>
                                            <input
                                                type="text"
                                                autoFocus
                                                placeholder="Venue name or city"
                                                value={form.location}
                                                onChange={e => set('location', e.target.value)}
                                                className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors mb-3"
                                            />
                                            <textarea
                                                rows={3}
                                                placeholder="Anything else — vibe, references, budget, timeline…"
                                                value={form.notes}
                                                onChange={e => set('notes', e.target.value)}
                                                className="w-full bg-white/5 rounded-none px-4 py-3 text-base sm:text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors resize-none"
                                            />
                                            <div className="bg-white/[0.04] p-3 mt-4">
                                                <p className="text-white/60 text-[11px] leading-relaxed">
                                                    Submitting holds the date while we talk. A{' '}
                                                    <span className="text-white font-bold">50% retainer</span>{' '}
                                                    finalizes the booking — contract and payment link follow after we scope it out.
                                                    Payment via{' '}
                                                    <span className="text-white font-bold">Stripe</span> or{' '}
                                                    <span className="text-white font-bold">Bitcoin</span>.
                                                </p>
                                                <p className="text-white/40 text-[11px] leading-relaxed mt-2">
                                                    The retainer is non-refundable once paid. Cancel more than 14 days out
                                                    and nothing further is owed; inside 14 days, 25% of the balance; inside
                                                    48 hours or a no-show, the full amount. One free reschedule with 48+
                                                    hours' notice. Full terms are in the contract.
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}

                                {!expressMode && detailsStep === TOTAL_DETAILS_STEPS - 1 && (
                                    <div className="mt-6">
                                        <BrandAssetPicker files={brandFiles} onChange={setBrandFiles} onError={setError} />
                                    </div>
                                )}

                                <TurnstileField
                                    action="booking"
                                    resetKey={turnstileKey}
                                    onToken={setTurnstileToken}
                                    onFailed={() => setError('The security check failed to load. Refresh the page and try again.')}
                                />
                                {error && (
                                    <p className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-4">{error}</p>
                                )}

                                <div className="flex gap-3 mt-6">
                                    {detailsStep > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setDetailsStep(s => s - 1)}
                                            className="px-5 py-3 bg-white/5 text-white text-[11px] font-black uppercase tracking-widest hover:bg-white/10 transition-colors"
                                        >
                                            Back
                                        </button>
                                    )}
                                    {detailsStep < TOTAL_DETAILS_STEPS - 1 ? (
                                        <button
                                            type="button"
                                            onClick={() => { if (canProceedDetails()) setDetailsStep(s => s + 1); }}
                                            disabled={!canProceedDetails()}
                                            className="flex-1 px-5 py-3 bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-white/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            Continue
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
                                            disabled={submitting}
                                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-white/80 transition-colors disabled:opacity-40"
                                        >
                                            {submitting ? 'Sending…' : (<>Submit Request <ArrowLongRightIcon className="w-4 h-4" /></>)}
                                        </button>
                                    )}
                                </div>
                                <p className="text-white/20 text-[9px] text-center uppercase tracking-widest mt-4">
                                    Response within 24 hours.
                                </p>
                            </motion.div>
                        )}

                        {/* Success */}
                        {step === 'success' && (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="max-w-md text-center py-16 space-y-6"
                            >
                                <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto" />
                                <h3 className="text-2xl font-black uppercase tracking-tight">Request Sent</h3>
                                <p className="text-white/50 text-sm leading-relaxed">
                                    <span className="text-white font-bold">{formatDate(form.event_date)}</span>{' '}
                                    is on hold while we talk. I'll reach out within 24 hours to confirm
                                    scope — nothing is finalized until the retainer is received.
                                </p>
                                {(brandLink || brandStatus.state !== 'idle') && (
                                    <div data-tour="booking-brand-assets-sent" className="bg-white/5 p-4 text-left space-y-3">
                                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/50">Brand assets</p>
                                        {brandStatus.state === 'sending' && <p className="text-white/70 text-sm">Uploading your files…</p>}
                                        {brandStatus.state === 'done' && (
                                            <p className="text-white text-sm">{brandStatus.received} file{brandStatus.received === 1 ? '' : 's'} received.</p>
                                        )}
                                        {brandStatus.state === 'error' && <p className="text-red-400 text-sm">{brandStatus.error}</p>}
                                        {brandStatus.state === 'idle' && (
                                            <p className="text-white/60 text-sm">Got a logo or brand guide we should match? Send it any time before the shoot.</p>
                                        )}
                                        {brandLink && (
                                            <a
                                                href={brandLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block px-4 py-3 bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-white/80 transition-colors"
                                                style={{ borderRadius: 0 }}
                                            >
                                                {brandStatus.state === 'done' ? 'Send more files' : 'Upload brand assets'}
                                            </a>
                                        )}
                                        {brandLink && <p className="text-white/30 text-[10px]">The link is in your confirmation email too.</p>}
                                    </div>
                                )}
                                {notifyDebug && (
                                    <div className="bg-yellow-500/10 text-yellow-300 text-[10px] leading-relaxed p-3 text-left">
                                        <p className="font-black uppercase tracking-widest mb-1">Admin notice</p>
                                        <p className="break-all">{notifyDebug}</p>
                                    </div>
                                )}
                                <p className="text-white/30 text-xs">
                                    Follow{' '}
                                    <a href="https://instagram.com/tlau.media" target="_blank" rel="noopener noreferrer" className="text-white underline">
                                        @tlau.media
                                    </a>{' '}
                                    for the latest work.
                                </p>
                                <button
                                    onClick={() => { setForm(EMPTY_FORM); setStep('calendar'); setBrandFiles([]); setBrandLink(null); setBrandStatus({ state: 'idle' }); }}
                                    className="text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-white transition-colors"
                                >
                                    Submit another request
                                </button>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </div>

        </div>
    );
};

export default BookingPage;
